import { useWatch } from 'react-hook-form';
import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { createOption, ContentSection } from '@mews-commander/ui';
import { lt, useLocalizeText } from '@mews/framework';
import { EnumSelectField, SelectField } from '@mews-commander/form';
import { TextKey } from '@mews-commander/enums';
import { PortfolioRateGroupOptions, PortfolioSettlementExtent } from '@mews-commander/data-access';
import { TextInput } from '@optimus-web/core';
import { getTimeClass } from '@mews-commander/utils';
import { SettlementConfigurationsFieldName } from '../../../../../../../rateGroups';
import { MaximumTimeForPaymentField } from '../../../../../../../rateGroups/components/forms/fields/MaximumTimeForPaymentField';
import { useSelectedEnterpriseIdsContext } from '../../contexts/SelectedEnterpriseIdsContext';
import { useIntersectingServicesSuspenseQuery } from '../../queries/intersectingServices';
import { RateGroupFormValues } from '../../schemas/rateGroupFormSchema';

export const PaymentConfigurationSection = () => {
    const localizeText = useLocalizeText();

    const { enterpriseIds } = useSelectedEnterpriseIdsContext();
    const { intersectingServices } = useIntersectingServicesSuspenseQuery({
        enterpriseIds,
    });

    const serviceKey: string = useWatch<RateGroupFormValues>({ name: 'serviceKey' });
    const timeUnit = pipe(
        intersectingServices,
        array.findFirst((service) => service.externalIdentifier === serviceKey),
        option.map(getTimeClass),
        option.getOrElseW(() => undefined)
    );

    return (
        <ContentSection title={lt('RateGroupConfiguration')} description={lt('RateGroupConfigurationDescription')}>
            <MaximumTimeForPaymentField
                name={SettlementConfigurationsFieldName.MaximumTime}
                autoFocus
                timeUnit={timeUnit}
            />
            <EnumSelectField
                enumClass={PortfolioSettlementExtent}
                enumPrefix="SettlementExtent"
                required
                label={lt('SettlementExtent')}
                name={SettlementConfigurationsFieldName.Extent}
                multiselect
            />
            <SelectField
                label={lt('Options')}
                name={SettlementConfigurationsFieldName.Options}
                options={[
                    createOption({
                        value: PortfolioRateGroupOptions.AutomaticDepositSettlement,
                        label: lt('RateGroupOptionsAutomaticTaxDeposit'),
                    }),
                    createOption({
                        value: PortfolioRateGroupOptions.CreateSettlementTask,
                        label: lt('RateGroupOptionsCreatePaymentTask'),
                    }),
                ]}
                multiselect
            />
            <TextInput
                name="currency"
                placeholder={localizeText({ textKey: TextKey.RateGroupSettlementConfigurationCurrencyProperty })}
                disabled
            />
        </ContentSection>
    );
};
